<template>
  <div class="syndicate-lines" @click.stop @mousedown.stop>
    <div class="syndicate-line-header">
      <span class="syndicate-no-label">Syndicate No.</span>
      <span class="share-pct-label">Share %</span>
    </div>
    <div v-for="(line, index) in localLines" :key="index" class="syndicate-line">
      <BaseInput
        :value="line.syndicate_no"
        type="text"
        size="sm"
        placeholder="Syndicate No."
        maxlength="16"
        class="syndicate-no-input"
        @input="(value) => onSyndicateNoInput(index, value)"
        @click.native.stop
        @mousedown.native.stop
      />
      <div class="share-input-wrapper">
        <BaseInput
          :value="line.shareText"
          type="text"
          size="sm"
          placeholder="Share"
          class="share-input"
          @keypress="(e) => onSharePctKeyPress(e)"
          @input="(value) => onSharePctInput(index, value)"
          @click.native.stop
          @mousedown.native.stop
        />
        <span class="share-percent">%</span>
      </div>
      <button
        v-if="line.syndicate_no || line.shareText"
        type="button"
        class="remove-line-btn"
        title="Remove this syndicate line"
        @click.stop="removeLine(index)"
      >&times;</button>
    </div>
  </div>
</template>

<script>
import { defineComponent, computed } from '@vue/composition-api'
import { BaseInput } from '@components/base'

// Up to 9 decimal places, matching GlobalUMRUnderwriterSyndicate.slip_share_pct
// (DecimalField(max_digits=12, decimal_places=9)) - a share like 12.123456789
// must round-trip exactly, not get clipped the way the old whole-percent
// input did. Up to 3 integer digits so 100 (the one value a share legitimately
// reaches) still fits, same bound the backend's own SHARE_DECIMAL_PLACES/
// quantize check enforces server-side - this is a UX nicety that stops an
// obviously-too-long value from being typed at all, not a replacement for it.
const SHARE_PATTERN = /^\d{0,3}(\.\d{0,9})?$/

export default defineComponent({
  name: 'SyndicateLinesInput',
  components: { BaseInput },
  props: {
    value: {
      type: Array,
      default: () => []
    }
  },
  emits: ['input'],
  setup(props, { emit }) {
    // Always one trailing blank row beyond whatever is already entered, so
    // there is always an obvious place to start adding another syndicate -
    // no separate "add" button needed, since typing into it is the add.
    // That blank row is never itself emitted (see emitLines' own filter,
    // which mirrors _reconcile_syndicate_lines' identical "placeholder row"
    // skip on the backend).
    const localLines = computed(() => {
      const lines = (props.value || []).map((line) => ({
        syndicate_no: line.syndicate_no || '',
        shareText:
          line.slip_share_pct === null || line.slip_share_pct === undefined
            ? ''
            : String(line.slip_share_pct)
      }))
      const last = lines[lines.length - 1]
      if (!last || last.syndicate_no || last.shareText) {
        lines.push({ syndicate_no: '', shareText: '' })
      }
      return lines
    })

    const emitLines = (lines) => {
      const cleaned = lines
        .filter((line) => line.syndicate_no || line.shareText)
        .map((line) => ({
          syndicate_no: line.syndicate_no,
          slip_share_pct: line.shareText === '' ? null : line.shareText
        }))
      emit('input', cleaned)
    }

    // Deliberately not debounced: a shared debounce across every row in this
    // list would collapse a quick edit to one row with a quick edit to
    // another (lodash's debounce tracks the latest call only, not one per
    // row), silently dropping whichever happened first. Nothing downstream
    // of this emit does a network call per keystroke - only the form's own
    // Save button does - so there is no cost being traded away by emitting
    // on every keystroke here.
    const onSyndicateNoInput = (index, value) => {
      const lines = localLines.value.map((l) => ({ ...l }))
      lines[index].syndicate_no = value.replace(/[^\d]/g, '')
      emitLines(lines)
    }

    const onSharePctInput = (index, value) => {
      if (!SHARE_PATTERN.test(value)) return
      const lines = localLines.value.map((l) => ({ ...l }))
      lines[index].shareText = value
      emitLines(lines)
    }

    const onSharePctKeyPress = (e) => {
      if (!/[0-9.]/.test(e.key)) {
        e.preventDefault()
        return
      }
      const input = e.target
      if (!input) return
      const current = input.value || ''
      const next =
        current.substring(0, input.selectionStart || 0) +
        e.key +
        current.substring(input.selectionEnd || 0)
      if (!SHARE_PATTERN.test(next)) {
        e.preventDefault()
      }
    }

    const removeLine = (index) => {
      const lines = localLines.value.filter((_, i) => i !== index)
      emitLines(lines)
    }

    return {
      localLines,
      onSyndicateNoInput,
      onSharePctInput,
      onSharePctKeyPress,
      removeLine
    }
  }
})
</script>

<style scoped>
.syndicate-lines {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 210px;
}

.syndicate-line {
  display: flex;
  align-items: center;
  white-space: nowrap;
  gap: 4px;
}

.syndicate-line-header {
  display: flex;
  align-items: center;
  white-space: nowrap;
  gap: 4px;
}

.syndicate-no-label,
.share-pct-label {
  font-size: 11px;
  color: #f9f9f9;
  overflow: hidden;
  text-overflow: ellipsis;
}

.syndicate-no-label {
  width: 95px;
  min-width: 95px;
}

.share-pct-label {
  width: 90px;
  min-width: 90px;
}

.syndicate-no-input {
  width: 95px !important;
  min-width: 95px;
}

.syndicate-no-input >>> .form-control {
  font-size: 11px;
  padding-right: 0.35rem;
  padding-left: 0.35rem;
}

.share-input-wrapper {
  display: flex;
  align-items: center;
  white-space: nowrap;
}

.share-input {
  width: 90px !important;
  min-width: 90px;
  margin-right: 4px;
}

.share-percent {
  font-size: 14px;
  color: #f9f9f9;
}

.remove-line-btn {
  background: none;
  border: none;
  color: #f9f9f9;
  font-size: 16px;
  line-height: 1;
  padding: 0 2px;
  cursor: pointer;
}
</style>
